import { useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetRoomSessionsForFilterQuery } from '../../apps/professional-app/fa/store/fa'
import { FilterAttribute, FilterOperator, type SessionFilter } from '../SessionFilters/model'
import { parseMetadata } from '../../apps/professional-app/fa/MeetingSummary/utils'
import { type ExternalUserConsentChange } from '../../models/User'

const MEETING_TITLES_PAGE_SIZE = 500

export const useMeetingTitles = (consentChanges: ExternalUserConsentChange[] | undefined) => {
  const accountId = useAppSelector(selectAccountId)

  const roomIdSessionIdPairs = useMemo<string[]>(() => {
    if (!consentChanges) {
      return []
    }
    const pairs = consentChanges.filter((c) => c.roomId && c.sessionId).map((c) => `${c.roomId}:${c.sessionId}`)
    return Array.from(new Set(pairs))
  }, [consentChanges])

  const skip = roomIdSessionIdPairs.length === 0
  const filter: SessionFilter = {
    type: 'individual',
    filter: { attr: FilterAttribute.ROOM_ID_SESSION_ID, op: FilterOperator.IN, val: roomIdSessionIdPairs.join(',') },
  }
  const {
    data: sessionsPage,
    isFetching,
    isError,
  } = useGetRoomSessionsForFilterQuery({ accountId, filter, pageRequest: { page: 0, size: MEETING_TITLES_PAGE_SIZE } }, { skip })

  const titles = useMemo<Record<string, string>>(() => {
    if (!sessionsPage) {
      return {}
    }
    return Object.fromEntries(
      sessionsPage.data.map((session) => {
        const metadata = parseMetadata(session.metadata)
        return [session.id, metadata?.external?.title ?? session.id]
      }),
    )
  }, [sessionsPage])

  return { titles, titlesLoaded: skip || (!isFetching && (isError || sessionsPage !== undefined)) }
}
